import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { FormulaCustomOperatorContributor } from "abreact/expression/formula/FormulaCustomOperatorContributor";
import { IfThenElseCustomOperandContributor } from "abreact/expression/ui/editor/impl/model/impl/conditional/IfThenElseCustomOperandContributor";
import { SummaryElementSet } from "abreact/expression/ui/editor/readonly/ExpressionSummaryElement";
import { ExpExpressionNodeNodeTypeEnum } from "abweb/generated/abwebinternal/api/dto/ExpExpressionNode";
export let SummaryMode = /*#__PURE__*/function (SummaryMode) {
  SummaryMode[SummaryMode["ALL_OPERANDS"] = 0] = "ALL_OPERANDS";
  SummaryMode[SummaryMode["EXCLUDE_ADVANCED"] = 1] = "EXCLUDE_ADVANCED";
  SummaryMode[SummaryMode["ADVANCED_ONLY"] = 2] = "ADVANCED_ONLY";
  return SummaryMode;
}({});
export class ExpressionSummaryModel {
  constructor() {
    _defineProperty(this, "_contentList", void 0);
    _defineProperty(this, "_currentModelElementSet", void 0);
    _defineProperty(this, "_depth", void 0);
    _defineProperty(this, "_renderDepthAmplification", 0);
    _defineProperty(this, "_customSubExpressionDepthAmplification", 0);
    _defineProperty(this, "_summaryMode", void 0);
    if (!ExpressionSummaryModel.hasCustomOperatorContributor(FormulaCustomOperatorContributor.IDENTIFIER)) {
      ExpressionSummaryModel.registerCustomOperatorContributor(new FormulaCustomOperatorContributor());
    }
    if (!ExpressionSummaryModel.hasCustomOperatorContributor(IfThenElseCustomOperandContributor.IDENTIFIER)) {
      ExpressionSummaryModel.registerCustomOperatorContributor(new IfThenElseCustomOperandContributor());
    }
  }
  init(rootNode) {
    this._depth = ExpressionSummaryModel.DEFAULT_DEPTH;
    if (rootNode.rootNodeType === ExpExpressionNodeNodeTypeEnum.ACTION_LIST) {
      this._depth = 1;
    }
    this._currentModelElementSet = new SummaryElementSet(this._depth);
    this._contentList = [this._currentModelElementSet];
  }
  get renderDepthAmplification() {
    return this._renderDepthAmplification;
  }

  /**
   * This will increase the rendering depth but not the logical depth of the expression nesting (which would
   * impact determination of need for parenthesis)
   *
   * @param value
   */
  set renderDepthAmplification(value) {
    this._renderDepthAmplification = value;
  }

  /**
   * If set, this will adjust depth of subexpression rendering to push it to the right when a subexpression
   * is rendered. It allows custom renderers to indicate that they want "upcoming sub expressions" to be indented
   */
  get customSubExpressionDepthAmplification() {
    return this._customSubExpressionDepthAmplification;
  }
  set customSubExpressionDepthAmplification(value) {
    this._customSubExpressionDepthAmplification = value;
  }
  static registerCustomOperatorContributor(generator) {
    ExpressionSummaryModel._customOperatorContributorMap.set(generator.identifier, generator);
  }
  static hasCustomOperatorContributor(identifier) {
    return ExpressionSummaryModel._customOperatorContributorMap.get(identifier) != null;
  }
  static getApplicableCustomOperatorContributor(node) {
    let applicableGenerator;
    this._customOperatorContributorMap.forEach(generator => {
      if (applicableGenerator == null && generator.appliesToNode(node)) {
        applicableGenerator = generator;
      }
    });
    return applicableGenerator;
  }
  populateFromStructure(expressionStructure, summaryMode) {
    this._summaryMode = summaryMode;
    if (expressionStructure.rootNode) {
      this.init(expressionStructure.rootNode);
      expressionStructure.rootNode.contribute(this, summaryMode);
    }
  }
  populateFromStructureNode(expressionStructureNode, summaryMode) {
    this._summaryMode = summaryMode;
    this.init(expressionStructureNode);
    expressionStructureNode.contribute(this, summaryMode);
  }
  get hasContent() {
    return this.content && this.content[0].elements.length > 0;
  }
  get summaryMode() {
    return this._summaryMode;
  }
  get depth() {
    return this._depth;
  }
  get hasCurrentRow() {
    return this._currentModelElementSet != null;
  }
  get currentRow() {
    if (!this._currentModelElementSet) {
      throw new Error("Access to first row requested before a row was started.");
    }
    return this._currentModelElementSet;
  }
  startNewRow() {
    this._currentModelElementSet = new SummaryElementSet(this._depth + this.renderDepthAmplification);
    this._contentList.push(this._currentModelElementSet);
    return this._currentModelElementSet;
  }
  incrementDepth() {
    this._depth++;
  }
  decrementDepth() {
    this._depth--;
  }
  get content() {
    return this._contentList;
  }
}
_defineProperty(ExpressionSummaryModel, "DEFAULT_DEPTH", 0);
_defineProperty(ExpressionSummaryModel, "_customOperatorContributorMap", new Map());
//# sourceMappingURL=ExpressionSummaryModel.js.map
